import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Volume2, VolumeX, Loader2, Play, Pause, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AudioPlayer({ practiceId, practiceTitle }) {
  const { user } = useAuth();
  const [audioStatus, setAudioStatus] = useState("checking"); // checking, not_generated, generating, ready, error
  const [audioSrc, setAudioSrc] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/practices/${practiceId}/audio`).then((r) => {
      if (r.data.status === "ready" && r.data.audio_base64) {
        setAudioSrc(`data:audio/mp3;base64,${r.data.audio_base64}`);
        setAudioStatus("ready");
      } else {
        setAudioStatus("not_generated");
      }
    }).catch(() => setAudioStatus("not_generated"));
  }, [practiceId]);

  const generateAudio = async () => {
    if (!user) {
      toast.error("Sign in to generate audio guides");
      return;
    }
    setAudioStatus("generating");
    try {
      await axios.post(`${API}/practices/${practiceId}/generate-audio`);
      const r = await axios.get(`${API}/practices/${practiceId}/audio`);
      if (r.data.status === "ready" && r.data.audio_base64) {
        setAudioSrc(`data:audio/mp3;base64,${r.data.audio_base64}`);
        setAudioStatus("ready");
        toast.success("Audio guide generated");
      }
    } catch (e) {
      setAudioStatus("error");
      toast.error(e.response?.data?.detail || "Failed to generate audio");
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const restart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = x / rect.width;
      audioRef.current.currentTime = pct * duration;
    }
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  if (audioStatus === "checking") return null;

  return (
    <div className="p-5 rounded-2xl bg-[#F0EDE6] border border-[#E3DDD2]" data-testid="audio-player">
      <div className="flex items-center gap-3 mb-3">
        <Volume2 className="w-5 h-5 text-[#8FA69A]" strokeWidth={1.5} />
        <h3 className="font-heading text-base font-medium text-[#1A221E]">Guided Audio Session</h3>
      </div>

      {audioStatus === "not_generated" && (
        <div>
          <p className="text-sm text-[#737E79] mb-3">Generate an AI-narrated audio guide for this practice.</p>
          <button
            onClick={generateAudio}
            data-testid="generate-audio-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#8FA69A] text-white rounded-full text-sm font-medium hover:bg-[#2C4A3B] transition-all duration-300"
          >
            <Volume2 className="w-4 h-4" strokeWidth={1.5} />
            Generate Audio Guide
          </button>
        </div>
      )}

      {audioStatus === "generating" && (
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-[#8FA69A]" />
          <p className="text-sm text-[#737E79]">Generating your guided audio session... This may take a moment.</p>
        </div>
      )}

      {audioStatus === "error" && (
        <div>
          <p className="text-sm text-red-500 mb-2">Audio generation failed. Please try again.</p>
          <button onClick={generateAudio} data-testid="retry-audio-btn" className="text-sm text-[#8FA69A] underline">Retry</button>
        </div>
      )}

      {audioStatus === "ready" && audioSrc && (
        <div>
          <audio
            ref={audioRef}
            src={audioSrc}
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => setPlaying(false)}
            onLoadedMetadata={handleTimeUpdate}
          />
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              data-testid="play-pause-btn"
              className="w-10 h-10 rounded-full bg-[#8FA69A] text-white flex items-center justify-center hover:bg-[#2C4A3B] transition-all duration-300 shrink-0"
            >
              {playing ? <Pause className="w-4 h-4" strokeWidth={1.5} /> : <Play className="w-4 h-4 ml-0.5" strokeWidth={1.5} />}
            </button>
            <div className="flex-1">
              <div
                className="h-2 bg-[#E3DDD2] rounded-full cursor-pointer relative"
                onClick={handleSeek}
                data-testid="audio-progress-bar"
              >
                <div
                  className="h-full bg-[#8FA69A] rounded-full transition-all"
                  style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-[#737E79]">{formatTime(progress)}</span>
                <span className="text-xs text-[#737E79]">{formatTime(duration)}</span>
              </div>
            </div>
            <button
              onClick={restart}
              data-testid="restart-audio-btn"
              className="p-2 rounded-full text-[#737E79] hover:bg-[#E3DDD2] transition-colors"
            >
              <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
